import { abs } from "@suse-o11y/abs";
import { cx } from "@suse-o11y/class-variance";
import { differenceInMilliseconds, secondsToMilliseconds } from "@suse-o11y/date-time";
import type { Time } from "@suse-o11y/date-time/time";
import { min } from "@suse-o11y/min";
import { type Signal, useComputed } from "@suse-o11y/signal";
import { timeFromPx } from "@suse-o11y/time-from-px";
import { timeToPx } from "@suse-o11y/time-to-px";
import { useHandler } from "@suse-o11y/use-handler";
import clamp from "lodash/clamp";
import React from "react";
import { useEvent as useOnEvent } from "react-use";

import { useTimelineContext, useTimelineRange } from "../_context";

import { TimelineControlElement } from "./TimelineControlElement";
import { TimelinePlayHead } from "./TimelinePlayHead";
import { useSetTimestampCursor } from "./useSetTimestampCursor";

const MINIMAL_BRUSH_INTERVAL_MS = secondsToMilliseconds(10);

export interface TimelineBrushAreaState {
	start: Time;
	end: Signal<number>;
}

export interface BrushPosition {
	startPx: number;
	endPx: number;
}

export interface TimelineBrushAreaProps {
	state: TimelineBrushAreaState;
	areaOffsetTop?: number;
	areaOffsetBottom?: number;
	onBrush: (left: Time, right: Time, position: BrushPosition) => void;
	onCancel: VoidFunction;
}

export const TimelineBrushArea: React.FC<TimelineBrushAreaProps> = ({
	state,
	areaOffsetTop = 28,
	areaOffsetBottom = 8,
	onBrush,
	onCancel,
}) => {
	const { stageWidth, refreshInterval, formatTimestamp } = useTimelineContext();
	const [timelineStart, timelineEnd, now] = useTimelineRange({
		// if the interval is too long, the play head formatted time updates too rarely
		interval: min(300, refreshInterval),
	});
	const endPx = useComputed((monitor) => clamp(state.end.read(monitor), 0, stageWidth), [state.end, stageWidth]);

	const startPx = timeToPx({
		clamped: true,
		time: state.start,
		width: stageWidth,
		interval: { start: timelineStart, end: timelineEnd },
	});

	const endTime = timeFromPx({
		px: endPx,
		width: stageWidth,
		interval: { start: timelineStart, end: timelineEnd },
	});

	const isShort = abs(differenceInMilliseconds(endTime, state.start)) < MINIMAL_BRUSH_INTERVAL_MS;

	useSetTimestampCursor(endTime);

	useOnEvent(
		"mousemove",
		useHandler((event: MouseEvent) => {
			state.end.write(event.clientX);
		}),
	);

	useOnEvent(
		"mouseup",
		useHandler(() => {
			const [left, right] = endTime < state.start ? [endTime, state.start] : [state.start, endTime];
			const [start, end] = endPx < startPx ? [endPx, startPx] : [startPx, endPx];

			if (left !== right && !isShort) {
				onBrush(left, right, { startPx: start, endPx: end });
			} else {
				onCancel();
			}
		}),
	);

	return (
		<TimelineControlElement
			className="pointer-events-none inset-y-0"
			style={{
				width: abs(startPx - endPx),
			}}
			x={min(startPx, endPx)}
		>
			{/* colored glass */}
			<div
				className={cx("absolute inset-x-0", isShort ? "bg-red-500/20" : "bg-(--base-blue-600)/20")}
				style={{
					top: areaOffsetTop,
					bottom: areaOffsetBottom,
				}}
			/>

			{/* static play head */}
			{/* left-full should be used instead right-0 because it makes the positioning nicer */}
			<TimelinePlayHead
				className={cx("inset-y-0", startPx < endPx ? "left-0" : "left-full")}
				theme={isShort ? "red" : "default"}
			>
				{formatTimestamp({ time: state.start, timelineStart, timelineEnd, now }, startPx)}
			</TimelinePlayHead>

			{/* it is crucial to draw the 'end' part last, because it should always appear on top of the `start` one */}
			{/* left-full should be used instead right-0 because it makes the positioning nicer */}
			<TimelinePlayHead
				className={cx("inset-y-0", startPx <= endPx ? "left-full" : "left-0")}
				theme={isShort ? "red" : "default"}
			>
				{isShort ? "interval too small" : formatTimestamp({ time: endTime, timelineStart, timelineEnd, now }, endPx)}
			</TimelinePlayHead>
		</TimelineControlElement>
	);
};
